import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
  /** Mark the field invalid without a message of its own, when a form-level alert names it. */
  invalid?: boolean;
}

const CONTROL =
  'min-h-12 w-full rounded-control border bg-white px-3.5 text-base text-ink placeholder:text-faint ' +
  'disabled:bg-surface disabled:text-faint';

/** A labelled text input. The label, hint and error are wired with `for`, `aria-describedby`, `aria-invalid`. */
export const Input = forwardRef<
  HTMLInputElement,
  FieldProps & InputHTMLAttributes<HTMLInputElement>
>(function Input({ label, hint, error, invalid = false, id, className, ...rest }, ref) {
  const generated = useId();
  const inputId = id ?? generated;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium text-ink">
        {label}
      </label>
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error || invalid ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={cn(CONTROL, error || invalid ? 'border-danger' : 'border-field', className)}
        {...rest}
      />
      {hint && (
        <p id={hintId} className="text-xs text-faint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
});

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/** A labelled native select: keyboard and screen-reader behaviour come from the platform. */
export const Select = forwardRef<
  HTMLSelectElement,
  FieldProps & {
    options: SelectOption[];
    placeholder?: string;
  } & SelectHTMLAttributes<HTMLSelectElement>
>(function Select({ label, hint, error, options, placeholder, id, className, ...rest }, ref) {
  const generated = useId();
  const selectId = id ?? generated;
  const hintId = `${selectId}-hint`;
  const errorId = `${selectId}-error`;
  const describedBy = [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ');

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={selectId} className="text-sm font-medium text-ink">
        {label}
      </label>
      <select
        ref={ref}
        id={selectId}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy || undefined}
        className={cn(CONTROL, error ? 'border-danger' : 'border-field', className)}
        {...rest}
      >
        {placeholder !== undefined && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
      </select>
      {hint && (
        <p id={hintId} className="text-xs text-faint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  );
});
